/**
 * AdminFinanceDashboard - Global Finance Management for Churches/Organization
 * Separate from events, but can work with them
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import CurrencySelect from '@/components/CurrencySelect';
import RecordForUnit from '@/components/finance/RecordForUnit';
import ReconciliationPanel from '@/components/finance/ReconciliationPanel';
import UnassignedPledges from '@/components/finance/UnassignedPledges';
import RequisitionsPanel from '@/components/finance/RequisitionsPanel';
import BudgetPanel from '@/components/finance/BudgetPanel';
import LegalEntitiesPanel from '@/components/finance/LegalEntitiesPanel';
import CurrencyOversight from '@/components/finance/CurrencyOversight';
import { getCurrencyMeta } from '@/lib/currencies';

// Icons
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, PiggyBank, CreditCard,
  Plus, Pencil, Trash2, Calendar, Users, Building2, Church,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Download,
  Filter, Search, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  FileText, Receipt, Banknote, Heart, HandCoins, Home, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helpers for organization finance
const financeAPI = {
  // Contributions (Tithes, Offerings)
  getContributions: async (token, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API_URL}/api/org/contributions?${query}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch contributions');
    return res.json();
  },
  createContribution: async (token, data) => {
    const res = await fetch(`${API_URL}/api/org/contributions`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create contribution');
    return res.json();
  },
  
  // Expenses
  getExpenses: async (token, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API_URL}/api/org/expenses?${query}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch expenses');
    return res.json();
  },
  createExpense: async (token, data) => {
    const res = await fetch(`${API_URL}/api/org/expenses`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create expense');
    return res.json();
  },
  
  // Summary/Dashboard
  getSummary: async (token, period = 'month', reportingCurrency = 'USD') => {
    const res = await fetch(`${API_URL}/api/org/finance/summary?period=${period}&reporting_currency=${reportingCurrency}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch summary');
    return res.json();
  },
  
  // Fund Campaigns
  getCampaigns: async (token) => {
    const res = await fetch(`${API_URL}/api/org/fund-campaigns`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch campaigns');
    return res.json();
  },
  createCampaign: async (token, data) => {
    const res = await fetch(`${API_URL}/api/org/fund-campaigns`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create campaign');
    return res.json();
  },

  // Categories
  getCategories: async () => {
    const res = await fetch(`${API_URL}/api/org/finance/categories`);
    if (!res.ok) throw new Error('Failed to fetch categories');
    return res.json();
  }
};

// Contribution categories
const CONTRIBUTION_CATEGORIES = [
  { code: 'tithe', name: 'Tithe', icon: PiggyBank, color: 'text-amber-500' },
  { code: 'offering', name: 'Offering', icon: HandCoins, color: 'text-green-500' },
  { code: 'missions', name: 'Missions', icon: Heart, color: 'text-red-500' },
  { code: 'building_fund', name: 'Building Fund', icon: Building2, color: 'text-blue-500' },
  { code: 'benevolence', name: 'Benevolence', icon: Users, color: 'text-purple-500' },
  { code: 'special', name: 'Special Offering', icon: DollarSign, color: 'text-indigo-500' },
  { code: 'thanksgiving', name: 'Thanksgiving', icon: Heart, color: 'text-pink-500' },
  { code: 'other', name: 'Other', icon: Wallet, color: 'text-slate-500' }
];

const EXPENSE_CATEGORIES = [
  { code: 'salaries', name: 'Salaries & Allowances' },
  { code: 'utilities', name: 'Utilities' },
  { code: 'rent', name: 'Rent/Lease' },
  { code: 'maintenance', name: 'Maintenance' },
  { code: 'ministry', name: 'Ministry Expenses' },
  { code: 'outreach', name: 'Outreach' },
  { code: 'supplies', name: 'Supplies' },
  { code: 'transport', name: 'Transport' },
  { code: 'welfare', name: 'Welfare' },
  { code: 'other', name: 'Other' }
];

// ============== Add Contribution Dialog ==============
const AddContributionDialog = ({ open, onOpenChange, onSuccess, homeCurrency = 'USD' }) => {
  const { token } = useAuthStore();
  const [form, setForm] = useState({
    unit_id: '',
    member_name: '',
    category: 'offering',
    amount: '',
    currency: homeCurrency,
    date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    reference_number: '',
    notes: '',
    is_anonymous: false,
    // Contact fields for receipt
    email: '',
    phone: '',
    whatsapp: '',
    send_receipt: false,
    receipt_method: 'email'
  });

  const mutation = useMutation({
    mutationFn: (data) => financeAPI.createContribution(token, data),
    onSuccess: () => {
      toast.success('Contribution recorded successfully!');
      onOpenChange(false);
      onSuccess?.();
      setForm({
        unit_id: form.unit_id,
        member_name: '',
        category: 'offering',
        amount: '',
        currency: homeCurrency,
        date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        reference_number: '',
        notes: '',
        is_anonymous: false,
        email: '',
        phone: '',
        whatsapp: '',
        send_receipt: false,
        receipt_method: 'email'
      });
    },
    onError: (err) => toast.error(err.message)
  });

  const handleSubmit = () => {
    if (!form.unit_id) {
      toast.error('Choose the unit this contribution belongs to');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    mutation.mutate({
      ...form,
      amount: parseFloat(form.amount)
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="h-5 w-5 text-green-500" />
            Record Contribution
          </DialogTitle>
          <DialogDescription>
            Record a tithe, offering, or other contribution
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RecordForUnit value={form.unit_id}
                         onChange={(v) => setForm((f) => ({ ...f, unit_id: v }))} />

          <div className="space-y-2">
            <Label>Contributor Name</Label>
            <Input
              value={form.member_name}
              onChange={(e) => setForm({ ...form, member_name: e.target.value })}
              placeholder="Enter name or leave blank for anonymous"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRIBUTION_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                data-testid="contribution-amount"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Currency *</Label>
            <CurrencySelect
              value={form.currency}
              onChange={(v) => setForm({ ...form, currency: v })}
              homeCurrency={homeCurrency}
              testId="contribution-currency"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="mobile">Mobile Money</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Reference/Receipt Number</Label>
            <Input
              value={form.reference_number}
              onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
              placeholder="Optional"
            />
          </div>

          {/* Contact Information Section */}
          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium text-muted-foreground mb-3 block">
              Contact Information (for receipt)
            </Label>
            
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">WhatsApp</Label>
                  <Input
                    type="tel"
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send_receipt"
                    checked={form.send_receipt}
                    onChange={(e) => setForm({ ...form, send_receipt: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="send_receipt" className="text-sm cursor-pointer">
                    Send Receipt
                  </Label>
                </div>
                
                {form.send_receipt && (
                  <Select 
                    value={form.receipt_method} 
                    onValueChange={(v) => setForm({ ...form, receipt_method: v })}
                  >
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Add Expense Dialog ==============
const AddExpenseDialog = ({ open, onOpenChange, onSuccess, homeCurrency = 'USD' }) => {
  const { token } = useAuthStore();
  const [form, setForm] = useState({
    unit_id: '',
    description: '',
    category: 'ministry',
    amount: '',
    currency: homeCurrency,
    date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    vendor: '',
    receipt_number: '',
    notes: ''
  });

  const mutation = useMutation({
    mutationFn: (data) => financeAPI.createExpense(token, data),
    onSuccess: () => {
      toast.success('Expense recorded successfully!');
      onOpenChange(false);
      onSuccess?.();
      setForm({
        unit_id: form.unit_id,
        description: '',
        category: 'ministry',
        amount: '',
        currency: homeCurrency,
        date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        vendor: '',
        receipt_number: '',
        notes: ''
      });
    },
    onError: (err) => toast.error(err.message)
  });

  const handleSubmit = () => {
    if (!form.unit_id) {
      toast.error('Choose the unit this expense belongs to');
      return;
    }
    if (!form.description) {
      toast.error('Please enter a description');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    mutation.mutate({
      ...form,
      amount: parseFloat(form.amount)
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-red-500" />
            Record Expense
          </DialogTitle>
          <DialogDescription>
            Record a ministry, operational, or other expense
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RecordForUnit value={form.unit_id}
                         onChange={(v) => setForm((f) => ({ ...f, unit_id: v }))} />

          <div className="space-y-2">
            <Label>Description *</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What was this expense for?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                data-testid="expense-amount"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Currency *</Label>
            <CurrencySelect
              value={form.currency}
              onChange={(v) => setForm({ ...form, currency: v })}
              homeCurrency={homeCurrency}
              testId="expense-currency"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="petty_cash">Petty Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Vendor/Payee</Label>
            <Input
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="Who was paid?"
            />
          </div>

          <div className="space-y-2">
            <Label>Receipt Number</Label>
            <Input
              value={form.receipt_number}
              onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
              placeholder="Optional"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Main Component ==============
const AdminFinanceDashboard = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('month');
  // The email alert links straight to the held-money screen, so the tab is
  // controlled rather than merely defaulted.
  const [tab, setTab] = useState(
    () => new URLSearchParams(window.location.search).get('tab') || 'overview');
  const [showAddContribution, setShowAddContribution] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    goal_amount: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: (data) => financeAPI.createCampaign(token, data),
    onSuccess: () => {
      toast.success('Campaign created successfully!');
      setShowAddCampaign(false);
      setCampaignForm({
        name: '',
        description: '',
        goal_amount: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      queryClient.invalidateQueries({ queryKey: ['fund-campaigns'] });
    },
    onError: (err) => toast.error(err.message || 'Failed to create campaign')
  });

  const handleCreateCampaign = () => {
    if (!campaignForm.name) {
      toast.error('Campaign name is required');
      return;
    }
    if (!campaignForm.goal_amount || parseFloat(campaignForm.goal_amount) <= 0) {
      toast.error('Please enter a valid goal amount');
      return;
    }
    createCampaignMutation.mutate({
      name: campaignForm.name,
      description: campaignForm.description,
      target_amount: parseFloat(campaignForm.goal_amount),
      start_date: campaignForm.start_date,
      end_date: campaignForm.end_date || null,
      category: 'special'
    });
  };

  // Platform / assembly home currency — the default for every money field
  const { data: currencyList } = useQuery({
    queryKey: ['currency-default'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/currency/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch currencies');
      return res.json();
    },
    enabled: !!token,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const homeCurrency = (currencyList?.default_currency || 'USD').toUpperCase();

  // Money recorded but not yet valued. Until this banner existed it was only
  // visible to whoever thought to open the currency screen.
  const { data: heldMoney } = useQuery({
    queryKey: ['held-money-banner'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/fx/held-contributions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('unavailable');
      return res.json();
    },
    enabled: !!token,
    refetchInterval: 120000,
    retry: false,
  });
  const heldCount = heldMoney?.count || 0;

  const [requisitionUnit, setRequisitionUnit] = useState('');
  const { data: orgUnits } = useQuery({
    queryKey: ['org-units-for-requisitions'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/org/units?limit=300`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('unavailable');
      return res.json();
    },
    enabled: !!token && (tab === 'requisitions' || tab === 'budgets'),
    retry: false,
  });

  // Fetch summary data
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['finance-summary', period, homeCurrency],
    queryFn: () => financeAPI.getSummary(token, period, homeCurrency),
    enabled: !!token,
    retry: false
  });

  // Fetch recent contributions
  const { data: contributions, isLoading: contribLoading } = useQuery({
    queryKey: ['contributions', { limit: 10 }],
    queryFn: () => financeAPI.getContributions(token, { limit: 10 }),
    enabled: !!token,
    retry: false
  });

  // Fetch recent expenses
  const { data: expenses, isLoading: expensesLoading } = useQuery({
    queryKey: ['expenses', { limit: 10 }],
    queryFn: () => financeAPI.getExpenses(token, { limit: 10 }),
    enabled: !!token,
    retry: false
  });

  // Fetch fund campaigns
  const { data: campaigns } = useQuery({
    queryKey: ['fund-campaigns'],
    queryFn: () => financeAPI.getCampaigns(token),
    enabled: !!token,
    retry: false
  });

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
    queryClient.invalidateQueries({ queryKey: ['contributions'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    queryClient.invalidateQueries({ queryKey: ['fund-campaigns'] });
    toast.success('Data refreshed');
  };

  const formatCurrency = (amount, code) => {
    const ccy = (code || homeCurrency || 'USD').toUpperCase();
    const meta = getCurrencyMeta(ccy);
    return `${meta.symbol}${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: meta.decimals ?? 2,
      maximumFractionDigits: meta.decimals ?? 2,
    })}`;
  };

  const CurrencyChips = ({ totals, counts, tone = 'slate' }) => {
    const entries = Object.entries(totals || {});
    if (entries.length === 0) return null;
    return (
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`currency-chips-${tone}`}>
        {entries.map(([code, total]) => (
          <span key={code} className="rounded-full border bg-white/70 px-2 py-0.5 text-[11px] text-slate-600" data-testid={`currency-chip-${tone}-${code}`}>
            <span className="font-semibold text-slate-800">{code}</span> {formatCurrency(total, code)}
            {counts?.[code] ? ` · ${counts[code]}` : ''}
          </span>
        ))}
      </div>
    );
  };

  // Calculate totals from fetched data (fallback if summary endpoint doesn't exist yet)
  const contributionsList = contributions?.contributions || contributions?.items || contributions || [];
  const expensesList = expenses?.expenses || expenses?.items || expenses || [];
  
  const incomeByCurrency = summary?.income_by_currency || {};
  const expenseByCurrency = summary?.expense_by_currency || {};
  const isMultiCurrency = Object.keys(incomeByCurrency).length + Object.keys(expenseByCurrency).length > 2
    || Object.keys(incomeByCurrency).length > 1 || Object.keys(expenseByCurrency).length > 1;

  const totalIncome = summary?.converted?.income?.grand_total ?? summary?.total_income ??
    contributionsList.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalExpenses = summary?.converted?.expenses?.grand_total ?? summary?.total_expenses ??
    expensesList.reduce((sum, e) => sum + (e.amount || 0), 0);
  const netBalance = totalIncome - totalExpenses;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="h-7 w-7 text-green-600" />
              Finance Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage tithes, offerings, expenses, and fund campaigns
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={refreshData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Income</p>
                  <div className="text-2xl font-bold text-green-600">
                    {summaryLoading ? <Skeleton className="h-8 w-24" /> : `${isMultiCurrency ? '≈ ' : ''}${formatCurrency(totalIncome, homeCurrency)}`}
                  </div>
                  <CurrencyChips totals={incomeByCurrency} counts={summary?.income_counts_by_currency} tone="income" />
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <ArrowUpRight className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <div className="text-2xl font-bold text-red-600">
                    {summaryLoading ? <Skeleton className="h-8 w-24" /> : `${isMultiCurrency ? '≈ ' : ''}${formatCurrency(totalExpenses, homeCurrency)}`}
                  </div>
                  <CurrencyChips totals={expenseByCurrency} counts={summary?.expense_counts_by_currency} tone="expense" />
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <ArrowDownRight className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Net Balance</p>
                  <div className={`text-2xl font-bold ${netBalance >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                    {summaryLoading ? <Skeleton className="h-8 w-24" /> : `${isMultiCurrency ? '≈ ' : ''}${formatCurrency(netBalance, homeCurrency)}`}
                  </div>
                  {isMultiCurrency && (
                    <p className="mt-1 text-[11px] text-slate-400">Converted to {homeCurrency}; per-currency figures are authoritative</p>
                  )}
                </div>
                <div className={`h-12 w-12 rounded-full ${netBalance >= 0 ? 'bg-blue-100' : 'bg-amber-100'} flex items-center justify-center`}>
                  <Wallet className={`h-6 w-6 ${netBalance >= 0 ? 'text-blue-600' : 'text-amber-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Campaigns</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {(campaigns?.items || campaigns || []).filter(c => c.status === 'active').length || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setShowAddContribution(true)} data-testid="add-contribution-btn">
            <Plus className="h-4 w-4 mr-2" />
            Record Contribution
          </Button>
          <Button variant="outline" onClick={() => setShowAddExpense(true)} data-testid="add-expense-btn">
            <Receipt className="h-4 w-4 mr-2" />
            Record Expense
          </Button>
          <Button variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
        </div>

        {/* Money waiting on a rate — raised where finance actually looks. */}
        {heldCount > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
               data-testid="held-money-banner">
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {heldCount} contribution{heldCount === 1 ? '' : 's'} waiting on an exchange rate
              </p>
              <p className="text-xs text-amber-800">
                {Object.entries(heldMoney?.by_currency || {})
                  .map(([ccy, t]) => `${ccy} ${Number(t.amount || 0).toLocaleString()}`)
                  .join(' · ')}
                {' '}· received and receipted, counted in no converted total until a rate is approved.
              </p>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => setTab('currency')} data-testid="held-money-banner-open">
              Price them now
            </Button>
          </div>
        ) : null}

        {/* Main Content Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contributions">Contributions</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="campaigns">Fund Campaigns</TabsTrigger>
            <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">
              Reconciliation
            </TabsTrigger>
            <TabsTrigger value="currency" data-testid="tab-currency">
              Currency &amp; settlement
              {heldCount > 0 ? (
                <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white"
                      data-testid="currency-tab-held-badge">
                  {heldCount}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="requisitions" data-testid="tab-requisitions">
              Requisitions
            </TabsTrigger>
            <TabsTrigger value="budgets" data-testid="tab-budgets">
              Budgets
            </TabsTrigger>
            <TabsTrigger value="entities" data-testid="tab-entities">
              Registered bodies
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Contributions */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-green-500" />
                      Recent Contributions
                    </CardTitle>
                    <Button variant="ghost" size="sm">View All</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {contribLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}
                    </div>
                  ) : contributionsList.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <HandCoins className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No contributions recorded yet</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {contributionsList.slice(0, 5).map((c, i) => (
                          <div key={c.id || i} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full bg-green-100`}>
                                <HandCoins className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{c.member_name || 'Anonymous'}</p>
                                <p className="text-xs text-muted-foreground capitalize">{c.category?.replace('_', ' ')}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-600">{formatCurrency(c.amount, c.currency)}</p>
                              <p className="text-xs text-muted-foreground">{c.date}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Recent Expenses */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-red-500" />
                      Recent Expenses
                    </CardTitle>
                    <Button variant="ghost" size="sm">View All</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {expensesLoading ? (
                    <div className="space-y-2">
                      {[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}
                    </div>
                  ) : expensesList.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No expenses recorded yet</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {expensesList.slice(0, 5).map((e, i) => (
                          <div key={e.id || i} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-full bg-red-100">
                                <Receipt className="h-4 w-4 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{e.description}</p>
                                <p className="text-xs text-muted-foreground capitalize">{e.category?.replace('_', ' ')}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-red-600">{formatCurrency(e.amount, e.currency)}</p>
                              <p className="text-xs text-muted-foreground">{e.date}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Contribution Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {CONTRIBUTION_CATEGORIES.map(cat => {
                    const catTotal = contributionsList
                      .filter(c => c.category === cat.code)
                      .reduce((sum, c) => sum + (c.amount || 0), 0);
                    const Icon = cat.icon;
                    return (
                      <div key={cat.code} className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`h-5 w-5 ${cat.color}`} />
                          <span className="font-medium text-sm">{cat.name}</span>
                        </div>
                        <p className="text-xl font-bold">{formatCurrency(catTotal)}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contributions Tab */}
          <TabsContent value="contributions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Contributions</CardTitle>
                  <Button onClick={() => setShowAddContribution(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {contribLoading ? (
                  <Skeleton className="h-64" />
                ) : contributionsList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <HandCoins className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <h3 className="font-medium mb-2">No contributions yet</h3>
                    <p className="text-sm mb-4">Start recording tithes, offerings, and other contributions</p>
                    <Button onClick={() => setShowAddContribution(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Record First Contribution
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Contributor</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Receipt</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contributionsList.map((c, i) => (
                        <TableRow key={c.id || i}>
                          <TableCell>{c.date}</TableCell>
                          <TableCell>{c.member_name || 'Anonymous'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">
                              {c.category?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{c.payment_method?.replace('_', ' ')}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {formatCurrency(c.amount, c.currency)}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.receipt_sent ? (
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Sent
                              </Badge>
                            ) : c.email ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/org/contributions/${c.id}/send-receipt`, {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    if (res.ok) {
                                      toast.success('Receipt sent!');
                                      queryClient.invalidateQueries({ queryKey: ['contributions'] });
                                    } else {
                                      toast.error('Failed to send receipt');
                                    }
                                  } catch (err) {
                                    toast.error('Error sending receipt');
                                  }
                                }}
                              >
                                <Mail className="h-3 w-3 mr-1" />
                                Send
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">No email</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Expenses</CardTitle>
                  <Button onClick={() => setShowAddExpense(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {expensesLoading ? (
                  <Skeleton className="h-64" />
                ) : expensesList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Receipt className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <h3 className="font-medium mb-2">No expenses yet</h3>
                    <p className="text-sm mb-4">Start tracking your organization's expenses</p>
                    <Button onClick={() => setShowAddExpense(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Record First Expense
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expensesList.map((e, i) => (
                        <TableRow key={e.id || i}>
                          <TableCell>{e.date}</TableCell>
                          <TableCell>{e.description}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">
                              {e.category?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{e.vendor || '-'}</TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {formatCurrency(e.amount, e.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fund Campaigns Tab */}
          <TabsContent value="reconciliation" className="space-y-4">
            <ReconciliationPanel />
            <UnassignedPledges />
          </TabsContent>

          <TabsContent value="currency">
            <CurrencyOversight />
          </TabsContent>

          <TabsContent value="campaigns">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Fund Campaigns</CardTitle>
                  <Button onClick={() => setShowAddCampaign(true)} data-testid="create-campaign-btn">
                    <Plus className="h-4 w-4 mr-2" /> Create Campaign
                  </Button>
                </div>
                <CardDescription>
                  Special fundraising campaigns for building projects, missions, etc.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(campaigns?.items || campaigns || []).length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <h3 className="font-medium mb-2">No active campaigns</h3>
                    <p className="text-sm">Create a fund campaign for special projects</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(campaigns?.items || campaigns || []).map((campaign, i) => (
                      <Card key={campaign.id || i}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-semibold">{campaign.name}</h3>
                              <p className="text-sm text-muted-foreground">{campaign.description}</p>
                            </div>
                            <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                              {campaign.status}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Raised</span>
                              <span className="font-medium">
                                {formatCurrency(campaign.raised_amount, campaign.currency)} / {formatCurrency(campaign.goal_amount, campaign.currency)}
                              </span>
                            </div>
                            <Progress 
                              value={((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100} 
                              className="h-2"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="requisitions" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Which unit</CardTitle>
                <CardDescription>
                  Requisitions belong to the unit that will spend the money
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={requisitionUnit} onValueChange={setRequisitionUnit}>
                  <SelectTrigger className="w-96" data-testid="requisition-unit-select">
                    <SelectValue placeholder="Choose a unit" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(orgUnits || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} · {u.level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
            <RequisitionsPanel unitId={requisitionUnit} />
          </TabsContent>

          <TabsContent value="budgets" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Which unit</CardTitle>
                <CardDescription>
                  A budget belongs to the unit that will spend it
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={requisitionUnit} onValueChange={setRequisitionUnit}>
                  <SelectTrigger className="w-96" data-testid="budget-unit-select">
                    <SelectValue placeholder="Choose a unit" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(orgUnits || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} · {u.level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
            <BudgetPanel unitId={requisitionUnit} />
          </TabsContent>

          <TabsContent value="entities" className="space-y-4">
            <LegalEntitiesPanel />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <AddContributionDialog
          key={`contrib-${homeCurrency}`}
          homeCurrency={homeCurrency}
          open={showAddContribution}
          onOpenChange={setShowAddContribution}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['contributions'] });
            queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
          }}
        />
        <AddExpenseDialog
          key={`expense-${homeCurrency}`}
          homeCurrency={homeCurrency}
          open={showAddExpense}
          onOpenChange={setShowAddExpense}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['expenses'] });
            queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
          }}
        />

        {/* Create Campaign Dialog */}
        <Dialog open={showAddCampaign} onOpenChange={setShowAddCampaign}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Create Fund Campaign
              </DialogTitle>
              <DialogDescription>
                Start a new fundraising campaign for a special project
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Campaign Name *</Label>
                <Input
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  placeholder="e.g., Church Building Fund"
                  data-testid="campaign-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  placeholder="Describe the purpose of this campaign..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Goal Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={campaignForm.goal_amount}
                  onChange={(e) => setCampaignForm({ ...campaignForm, goal_amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="campaign-goal-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date (optional)</Label>
                  <Input
                    type="date"
                    value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCampaign(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCampaign}
                disabled={createCampaignMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-campaign-submit-btn"
              >
                {createCampaignMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Campaign'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminFinanceDashboard;
